import FormModel from "./FormModel";
import extend from "lodash/extend";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import { extendObservable } from "mobx";

// e.g. FormModelFactory.form(props)
//          .withValidations({
//              state: validations.isRequired("state"),
//              country: validations.isRequired("country"),
//          })
//          .withSelectOptions(["stateSelect", "countrySelect"])
//          .create();

// these functions will be wrapped to return a chainable context
const FormModelFactory = {
    form(props, formContext) {
        props = isEmpty(props) ? { data: {} } : props;
        formContext.formProps = props;
    },

    storageProvider(storageProvider, formContext) {
        formContext.storageProvider = storageProvider;
    },

    withValidations(validationsObject, formContext) {
        formContext.validations = validationsObject;
    },

    withValidationSteps(validationStepsObject, formContext) {
        formContext.validationSteps = validationStepsObject;
    },

    withMessageContainerId(messageContainerId, formContext) {
        formContext.messageContainerId = messageContainerId;
    },

    withPassThroughProps(propList, formContext) {
        formContext.passThroughProps = propList;
    },

    withParent(parentForm, formContext) {
        formContext.formProps = extend(formContext.formProps, { parent: parentForm });
    },

    withDirtyTracking(formContext) {
        formContext.enableDirtyTracking = true;
    }
};

// Wrap all of the validations so that they are chainable.
const wrapFunction = function(chainableFn) {
    return function() {
        let context = extend({ __factoryContext: true, fn: chainableFn, args: arguments }, FormModelFactory);
        if (this.__factoryContext) {
            this.next = context;
            context.root = this.root;
        } else {
            context.root = context;
        }
        return context;
    };
};

// do the wrapping
forEach(Object.keys(FormModelFactory), key => { FormModelFactory[key] = wrapFunction(FormModelFactory[key]); });

// And add the create() method to finish the chain into a FormModel class
FormModelFactory.create = function() {
    if (!this.__factoryContext) {
        throw new Error("Invalid FormModelFactory chain");
    }

    const formContext = {};
    let next = this.root;
    while (next) {
        // call everything in the chain with formContext
        const args = [].concat(Array.prototype.slice.apply(next.args)).concat([formContext]);
        next.fn(...args);
        next = next.next;
    }

    const FormModelChild = class extends FormModel {
        constructor(props, storageProvider) {
            super(props, storageProvider, FormModelChild.validations());
            if (formContext.passThroughProps) {
                extendObservable(this, pick(props, formContext.passThroughProps));
            }
            if (formContext.enableDirtyTracking) {
                this.setOriginalData();
            }
            if (formContext.messageContainerId) {
                this.messageContainerId = formContext.messageContainerId;
            }
        }

        static validations() {
            return formContext.validations ? formContext.validations : {};
        }

        get validationSteps() {
            if (formContext.validationSteps) {
                return formContext.validationSteps;
            }
            return super.validationSteps;
        }
    };
    return new FormModelChild(formContext.formProps, formContext.storageProvider);
};

export default FormModelFactory;
